import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { markGeoLanguageDetected } from '../utils/geoLanguageDetector';

interface LanguageSelectorProps {
  variant?: 'header' | 'sidebar';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'header' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
    { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    // Mark that user has manually selected a language (prevents auto-detection override)
    markGeoLanguageDetected();
    setIsOpen(false);
  };

  // Sidebar variant - full width button with dropdown going up
  if (variant === 'sidebar') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label={`Select language. Current: ${currentLanguage.name}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/5"
        >
          <div className="flex items-center gap-2">
            <span className="text-base" aria-hidden="true">{currentLanguage.flag}</span>
            <span>{currentLanguage.name}</span>
          </div>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            
            {/* Dropdown - opens upward in sidebar */}
            <div 
              role="listbox" 
              aria-label="Select language"
              className="absolute left-0 bottom-full mb-1 w-full bg-dark-800 border border-dark-700 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto"
            >
              {languages.map((language) => (
                <button
                  key={language.code}
                  role="option"
                  aria-selected={i18n.language === language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left text-sm transition-colors duration-200 ${
                    i18n.language === language.code
                      ? 'bg-primary-600/20 text-primary-400'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="text-lg" aria-hidden="true">{language.flag}</span>
                  <span>{language.name}</span>
                  {i18n.language === language.code && (
                    <svg className="w-4 h-4 ml-auto text-primary-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Header variant (default)
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Select language. Current: ${currentLanguage.name}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="flex items-center gap-1 px-1.5 lg:px-3 py-1 rounded text-xs font-medium transition-colors duration-200 bg-gray-600 hover:bg-gray-700 text-white"
      >
        <span className="text-sm" aria-hidden="true">{currentLanguage.flag}</span>
        <span className="hidden lg:block">{currentLanguage.name}</span>
        <svg
          className={`w-2.5 h-2.5 lg:w-3 lg:h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Dropdown */}
          <div 
            role="listbox" 
            aria-label="Select language"
            className="absolute right-0 top-full mt-1 w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                role="option"
                aria-selected={i18n.language === language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-left text-sm transition-colors duration-200 ${
                  i18n.language === language.code
                    ? 'bg-green-600 text-white'
                    : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <span className="text-lg" aria-hidden="true">{language.flag}</span>
                <span>{language.name}</span>
                {i18n.language === language.code && (
                  <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
